import { Component, OnInit, Inject, ViewChild, OnDestroy, Input, QueryList, ElementRef, ViewChildren } from '@angular/core';
import { SideNavService } from '@app/services/side-nav/side-nav.service';
import { distinctUntilChanged, first, map, skipWhile, startWith, switchMap, take, tap } from 'rxjs/operators';
import { CategoriesService } from '@app/services/categories/categories.service';
import * as _ from 'lodash';
import { CdkDrag, CdkDragDrop, CdkDragMove, moveItemInArray } from '@angular/cdk/drag-drop';
import { ProductStoredAreasService } from '@app/services/product-stored-areas/product-stored-areas.service';
import { SubSink } from 'subsink';
import { Store, select } from '@ngrx/store';
import * as OrganizationActions from '@app/modules/organization/store/actions/organization.actions';
import { selectOrganizationState, selectCategoriesState, selectAreasState, selectCurrentItemState, selectInCat, selectIsLoading, selectNotInCat, selectUncategorized } from '@app/modules/organization/store/selectors/organization.selectors';
import { FormComponent as CategoryForm } from '@app/modules/organization/categories-table/form/form.component';
import { FormComponent as AreaForm } from '@app/modules/organization/areas-table/form/form.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { merge } from 'rxjs';
import { selectProductsState } from '@app/modules/products/store/selectors/products.selectors';
import { BreakpointService } from '@app/services/breakpoint/breakpoint.service';
import { IArea } from '@app/models/area.interface';
import { ICategory } from '@app/models/category.interface';
import { LimitAccessService } from '@app/core/services/limit-access.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
const speed = 10;

@Component({
  selector: 'app-master-sorter',
  templateUrl: './master-sorter.component.html',
  styleUrls: ['./master-sorter.component.scss']
})
export class MasterSorterComponent implements OnInit, OnDestroy {
  public rippleRemoveColor: string = '#37b5486e';
  public rippleAddColor: string = '#37b5486e';
  public inCat$: any = [];
  public notInCat$: any = [];
  public currentCategory: { name: string, id: number } = { name: '', id: 0 }
  public uncategorized$: any[] = [];
  public leftValue = '';
  public rightValue = '';
  public subs: SubSink = new SubSink;
  public isLoading$: boolean = true;
  public selectedSection$: number = 0;
  public isHandset$: boolean = false;
  public columns$: number[] = [];
  public search$;
  public areas$: IArea[] = [];
  public categories$: ICategory[]=[];
  public itemsLength: number = 0;
  public lastItemText: string = '';
  public message: string = "It appears there aren't any Products associated with this Category.";
  public readOption: boolean = false;
  public addOption: boolean = false;
  public editOption: boolean = false;
  public deleteOption: boolean = false;
  public readProductOption: boolean = false;
  public addProductOption: boolean = false;
  public editProductOption: boolean = false;

  @Input() type;
  @Input() category: any; // Optional: for dialog mode, pass specific category
  @ViewChild('cdkDropList') cdkDropList;
  @ViewChild('scrollEl') scrollEl: ElementRef<HTMLElement>;
  @ViewChildren(CdkDrag) dragEls: QueryList<CdkDrag>;

  constructor(
    public _sideNav: SideNavService,
    private store$: Store,
    private _matDialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private _breakpoints: BreakpointService,
    public _router: Router,
    public _toastr: ToastrService,
    public _translation: TranslateService,
    private limitAccessService: LimitAccessService
  ) {

    if (localStorage.getItem('trakrCounting-prdCodes')) {
      this.columns$ = localStorage.getItem('trakrCounting-prdCodes').split('').map(item => parseInt(item));
    }
  }

  ngOnInit(): void {
    this.lastItemText = this.type == 'category' ? 'categories, one category must remain' : 'areas, one area must remain';
    this.subs.add(this.breakpointObserver.observe(Breakpoints.Small).pipe(map((result: BreakpointState) => result.matches)).subscribe(isHandset => this.isHandset$ = isHandset));
    this.subs.add(this.store$.select(selectOrganizationState).subscribe(({ section }) => this.selectedSection$ = section));
    this.subs.add(this.store$.select(selectInCat).subscribe(res => {
      this.inCat$ = res;
    }));
    this.subs.add(this.store$.select(selectNotInCat).subscribe(res => {
      this.notInCat$ = res;
    }));
    this.subs.add(this.store$.select(selectIsLoading).subscribe(res => {
      this.onClearLeftValue();
      this.onClearRightValue();
      this.isLoading$ = res;
    }));
    this.subs.add(this.store$.select(selectUncategorized).subscribe(res => {
      this.uncategorized$ = res;
    }));

    switch (this.type) {
      case 'category':
        this.subs.add(this.store$.select(selectCategoriesState).pipe(
          skipWhile(res => res == null),
          distinctUntilChanged((a, b) => a.length === b.length)
        ).subscribe(categories => {
          this.categories$ = categories;
          this.itemsLength = categories.length;
          // If category input is provided (dialog mode), use it; otherwise use first category
          const categoryId = this.category?.prdCatID || categories[0]?.prdCatID;
          this.store$.dispatch(OrganizationActions.loadMasterSortCategories({ prdCatID: categoryId }));
        }));

        this.subs.add(this.store$.select(selectCurrentItemState).pipe(
          skipWhile(res => res == null)
        ).subscribe((res) => {
          this.currentCategory.name = res.prdCatNameEn;
          this.currentCategory.id = res.prdCatID;
        }));

        this.subs.add(this.store$.select(selectProductsState).pipe(first()).subscribe(res => {
          if (res.total == 0) {
            this.message = "categories.products"
          } else {
            this.message = "available"
          }
        }));

        this.limitAccessService.arraySettingAccess$.subscribe((arraySettingAccess) => {
          if (arraySettingAccess) {
            this.readOption = this.limitAccessService.getAccess('products', 'categories', '','');
            this.addOption = this.limitAccessService.getAccess('products', 'categories', 'add','');
            this.editOption = this.limitAccessService.getAccess('products', 'categories', 'edit','');
            this.deleteOption = this.limitAccessService.getAccess('products', 'categories', 'delete','');

            this.readProductOption = this.limitAccessService.getAccess('products', 'products', '','');
            this.addProductOption = this.limitAccessService.getAccess('products', 'products', 'add','');
            this.editProductOption = this.limitAccessService.getAccess('products', 'products', 'edit','');

            if(this.readOption==false){
              this._router.navigateByUrl("/");
            }
          }
        });

        break;
      case 'area':
        this.subs.add((this.store$.select(selectAreasState).pipe(
          skipWhile(res => res == null),
          distinctUntilChanged((a, b) => a.length === b.length)
        ).subscribe(areas => {
          this.areas$ = areas;
          this.itemsLength = areas.length
          this.store$.dispatch(OrganizationActions.loadMasterSortAreas({ prdAreaID: areas[0]?.prdAreaID }))
        })));

        this.subs.add(this.store$.select(selectCurrentItemState).pipe(skipWhile(res => res == null)).subscribe((res) => {
          this.currentCategory.name = res.prdAreaNameEn;
          this.currentCategory.id = res?.prdAreaID;
        }));

        this.limitAccessService.arraySettingAccess$.subscribe((arraySettingAccess) => {
          if (arraySettingAccess) {
            this.readOption = this.limitAccessService.getAccess('products', 'areas', '','');
            this.addOption = this.limitAccessService.getAccess('products', 'areas', 'add','');
            this.editOption = this.limitAccessService.getAccess('products', 'areas', 'edit','');
            this.deleteOption = this.limitAccessService.getAccess('products', 'areas', 'delete','');

            this.readProductOption = this.limitAccessService.getAccess('products', 'products', '','');
            this.addProductOption = this.limitAccessService.getAccess('products', 'products', 'add','');
            this.editProductOption = this.limitAccessService.getAccess('products', 'products', 'edit','');
    
            if(this.readOption==false){
              this._router.navigateByUrl("/");
            }
          }
        });

        break;
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onOpenMasterCategorizer = (name, id, type) => {
    this.type = type;
    this.currentCategory.id = id;
  }

  onDrop(event: CdkDragDrop<string[]>) {
    if(this.editProductOption){
      moveItemInArray(this.inCat$, event.previousIndex, event.currentIndex);
      if (this.type == 'area') {
        this.store$.dispatch(OrganizationActions.sortAreas({ products: this.inCat$, area: this.currentCategory.id }));
      } else {
        this.store$.dispatch(OrganizationActions.sortCategories({ products: this.inCat$ }));
      }
    }
    else{
      this._toastr.error(this._translation.instant('permission.product.error'));
    }
  }

  onSort() {
    switch (this.type) {
      case 'category':
        this.store$.dispatch(OrganizationActions.sortCategoryByName({ prdCatID: this.currentCategory.id }));
        break;
      case 'area':
        this.store$.dispatch(OrganizationActions.sortAreaByName({ prdAreaID: this.currentCategory.id }));
        break;
    }
  }

  onGetByCategory(id) {
    this.store$.dispatch(OrganizationActions.loadMasterSortCategories({ prdCatID: id }))
  }

  onSetPrdCode(column: number) {
    switch (column) {
      case 2:
        this.onClearLeftValue();
        break;
      case 3:
        this.onClearRightValue();
        break;
    }
    this.columns$.push(column)
    localStorage.setItem('trakrCounting-prdCodes', this.columns$.join(''));
  }

  onRemovePrdCode(column: number) {
    switch (column) {
      case 2:
        this.onClearLeftValue();
        break;
      case 3:
        this.onClearRightValue();
        break;
    }
    this.columns$ = this.columns$.filter(item => item != column);
    localStorage.setItem('trakrCounting-prdCodes', this.columns$.join(''));
  }

  onMoveCategoryItems(category, index) {
    if(this.editProductOption){
      this.store$.dispatch(OrganizationActions.addAllFromMasterSortAreas({ products: this.notInCat$[index].products, prdCatID: category.prdCatID }));
    }
    else{
      this._toastr.error(this._translation.instant('permission.product.error'));
    }
  }

  onMoveUncategorizedItems() {
    if(this.editProductOption){
      this.store$.dispatch(OrganizationActions.addUncategorizedFromMasterSortAreas({ products: this.uncategorized$ }));
    }
    else{
      this._toastr.error(this._translation.instant('permission.product.error'));
    }
  }

  onRemoveFromUncategorized(index, product) {
    if(this.editProductOption){
      this.store$.dispatch(OrganizationActions.addFromMasterSortAreas({ product: product }));
    }
    else{
      this._toastr.error(this._translation.instant('permission.product.error'));
    }
  }

  onToggleLeftSelection(product) {
    if(this.editProductOption){
      switch (this.type) {
        case "area":
          this.store$.dispatch(OrganizationActions.removeFromMasterSortAreas({ product: product }));
          break;
        case "category":
          this.store$.dispatch(OrganizationActions.removeFromMasterSortCategories({ product: product, id: this.currentCategory.id }));
          break;
      }
    }
    else{
      this._toastr.error(this._translation.instant('permission.product.error'));
    }
  }

  onToggleRightSelection = (index, productIndex, product) => {
    if(this.editProductOption){
      switch (this.type) {
        case "area":
          this.store$.dispatch(OrganizationActions.addFromMasterSortAreas({ product: product }));
          break;
        case "category":
          this.store$.dispatch(OrganizationActions.addFromMasterSortCategories({ product: product }));
          break
      }
    }
    else{
      this._toastr.error(this._translation.instant('permission.product.error'));
    }
  }

  private onFilterProducts(product) {
    switch (this.type) {
      case "category":
        this.inCat$.push(product);
        break;
      case "area":
        this.inCat$.push(product);
        break;
    }
  }

  onClearLeftValue() {
    this.leftValue = '';
    this.store$.dispatch(OrganizationActions.resetQueryIn());
  }

  onClearRightValue() {
    this.rightValue = '';
    this.store$.dispatch(OrganizationActions.resetQueryNotIn());
  }

  onApplyFilterInCategory(event) {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
    if (filterValue == "") return this.store$.dispatch(OrganizationActions.resetQueryIn())
    switch (this.type) {
      case "area":
        this.store$.dispatch(OrganizationActions.setQueryIn({ query: filterValue }));
        break;
      case "category":
        this.store$.dispatch(OrganizationActions.setQueryIn({ query: filterValue }));
        break;
    }
  }

  onApplyFilterNotInCategory(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
    if (filterValue == "") return this.store$.dispatch(OrganizationActions.resetQueryNotIn());
    switch (this.type) {
      case "area":
        this.store$.dispatch(OrganizationActions.setQueryNotInArea({ query: filterValue }));
        break;
      case "category":
        this.store$.dispatch(OrganizationActions.setQueryNotInCategory({ query: filterValue }));
        break;
      default:
        return;
    }
  }

  createRange(number) {
    return new Array(number);
  }

  onEdit(row) {
    switch (this.type) {
      case 'category':
        row.prdCatNameEn = row.name;
        row.prdCatID = row.id;
        this._matDialog.open(CategoryForm, {
          data: {
            type: "Edit",
            area: row,
          },
          width: this._breakpoints.isMobile() === true ? "100vw" : "30vw",
          panelClass: 'overflow-hidden'
        });
        break;
      case 'area':
        row.prdAreaNameEn = row.name;
        row.prdAreaID = row.id;
        this._matDialog.open(AreaForm, {
          data: {
            type: "Edit",
            area: row,
          },
          width: this._breakpoints.isMobile() === true ? "100vw" : "30vw",
          panelClass: 'overflow-hidden'
        });
        break;
    }
  }

  onDelete(category) {
    const dialogRef = this._matDialog.open(ConfirmModalComponent, {
      data: {
        key: 'sorter',
        type: this.type,
        item: `${category.name}`,
        deletePassword: 'DELETE'
      },
      minWidth: '30vw',
      maxWidth: '700px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result == 'true') {
        switch (this.type) {
          case 'category':
            this.store$.dispatch(OrganizationActions.deleteCategory());
            break;
          case 'area':
            this.store$.dispatch(OrganizationActions.deleteArea());
            break;
        }
      }
    });
  }



  ngAfterViewInit() {
    // const onMove$ = this.dragEls.changes.pipe(
    //   startWith(this.dragEls)
    //   , map((d: QueryList<CdkDrag>) => d.toArray())
    //   , map(dragels => dragels.map(drag => drag.moved))
    //   , switchMap(obs => merge(...obs))
    // );

    // this.subs.add(onMove$.subscribe());

    // const onDown$ = this.dragEls.changes.pipe(
    //   startWith(this.dragEls)
    //   , map((d: QueryList<CdkDrag>) => d.toArray())
    //   , map(dragels => dragels.map(drag => drag.ended))
    //   , switchMap(obs => merge(...obs))
    // );

    // this.subs.add(onDown$.subscribe());
  }
}

